/**
 * Sound settings — master / effects / music, each a level plus a mute.
 *
 * ─── What these actually scale, as of the mixer landing ─────────────────────
 * `engine/audio/mixer.ts` builds one gain node per channel and drives both
 * from `effectiveVolume(...)` below, so these sliders are live. What is
 * routed through them today:
 *   effects — the campfire crackle at every authored fire (`fire-ambience.ts`)
 *   music   — the settlement ambient bed, in `home_settlement` only
 * The two PvP scores ship in `engine/audio/tracks.ts` but are not started by
 * anything; the reason is documented on `TRACKS.pvpStealth` and is a missing
 * server signal, not a missing slider.
 *
 * Master is a MULTIPLIER over the other two rather than a third bus — see
 * `effectiveVolume`, which is the single place that rule is expressed. Do not
 * re-derive it at a call site.
 *
 * ─── Why localStorage and not the server ────────────────────────────────────
 * Volume is a property of the DEVICE, not the account: the same player wants
 * a different level on a laptop in a café than on a desktop with speakers.
 * Core's `PATCH /me/preferences` takes locale and currency and nothing else,
 * so a server-side home for this would mean a new column and a new route for
 * something that should not follow the account anyway.
 *
 * Note this is NOT the auth-token rule being bent — that rule is about
 * credentials (`token-store.ts` keeps the access token in memory precisely so
 * it never touches storage). A volume level is not a credential.
 */
import { createStore } from '../../core/state/store';

/** One mixer channel. `level` is 0..1; `muted` is independent of it. */
export interface AudioChannel {
  level: number;
  muted: boolean;
}

export interface AudioSettings {
  master: AudioChannel;
  effects: AudioChannel;
  music: AudioChannel;
}

export type AudioChannelId = keyof AudioSettings;

/** Order is render order in the Sound tab; master first because it scales the others. */
export const AUDIO_CHANNELS: { id: AudioChannelId; label: string; hint: string }[] = [
  { id: 'master', label: 'Master', hint: 'Scales everything below it.' },
  { id: 'effects', label: 'Effects', hint: 'Footsteps, gunfire, doors, pickups.' },
  { id: 'music', label: 'Music', hint: 'Ambient score.' },
];

const STORAGE_KEY = 'ah.audio-settings';

const DEFAULTS: AudioSettings = {
  // Not 1.0. A survival game that opens at full blast on a shared machine is
  // a worse first impression than one that opens a little quiet, and the
  // slider is right there.
  master: { level: 0.7, muted: false },
  effects: { level: 0.8, muted: false },
  music: { level: 0.5, muted: false },
};

export const audioSettingsStore = createStore<AudioSettings>(load());

/**
 * Reads the stored settings, falling back to the defaults on anything
 * unexpected.
 *
 * Every field is re-validated rather than trusted: localStorage is editable by
 * hand and survives across versions, so a stored `level: "loud"` or a channel
 * this build no longer has must not reach the rest of the app. A value out of
 * range is clamped, not rejected — the player's intent is clear.
 */
function load(): AudioSettings {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Record<AudioChannelId, Partial<AudioChannel>>>;
    return {
      master: readChannel(parsed.master, DEFAULTS.master),
      effects: readChannel(parsed.effects, DEFAULTS.effects),
      music: readChannel(parsed.music, DEFAULTS.music),
    };
  } catch {
    // Unparseable JSON, or storage blocked entirely (private mode, an iframe
    // with no access). Defaults are a working state; an exception here would
    // take the whole settings screen down with it.
    return { ...DEFAULTS };
  }
}

function readChannel(raw: Partial<AudioChannel> | undefined, fallback: AudioChannel): AudioChannel {
  const level = typeof raw?.level === 'number' && Number.isFinite(raw.level)
    ? Math.min(1, Math.max(0, raw.level))
    : fallback.level;
  return { level, muted: raw?.muted === true };
}

function persist(settings: AudioSettings): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage full or blocked. The in-memory store is still correct for this
    // session, which is the part the player can see.
  }
}

export function setChannelLevel(id: AudioChannelId, level: number): void {
  // NaN survives Math.min/Math.max — `Math.min(1, Math.max(0, NaN))` is NaN,
  // not 0 — so a non-finite input would be written straight through and later
  // multiplied into a gain node. Checked explicitly.
  const clamped = Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : 0;
  audioSettingsStore.set((s) => {
    const next = { ...s, [id]: { ...s[id], level: clamped } };
    persist(next);
    return next;
  });
}

export function setChannelMuted(id: AudioChannelId, muted: boolean): void {
  audioSettingsStore.set((s) => {
    const next = { ...s, [id]: { ...s[id], muted } };
    persist(next);
    return next;
  });
}

export function toggleChannelMuted(id: AudioChannelId): void {
  setChannelMuted(id, !audioSettingsStore.get()[id].muted);
}

/**
 * What a sound on `id` should actually play at, 0..1.
 *
 * The one function a future mixer calls. Master is a MULTIPLIER over the
 * channel rather than a fourth independent level, and either mute silences —
 * muting master has to silence music, or "mute" means nothing.
 *
 * Mute is kept separate from `level: 0` on purpose: a player who mutes and
 * unmutes expects their level back, and collapsing the two loses it.
 */
export function effectiveVolume(settings: AudioSettings, id: AudioChannelId): number {
  if (id === 'master') return settings.master.muted ? 0 : settings.master.level;
  if (settings.master.muted || settings[id].muted) return 0;
  return settings.master.level * settings[id].level;
}

/**
 * Re-reads the stored settings.
 *
 * Exists because `load()` runs once at module import and is otherwise
 * unreachable — which would leave the validation it does (the part that stops
 * a hand-edited `level: "loud"` reaching a future mixer) untestable, and
 * therefore effectively unowned.
 */
export function reloadAudioSettings(): void {
  audioSettingsStore.set(load());
}

/** Tests only — restores the shipped defaults and clears the stored copy. */
export function resetAudioSettings(): void {
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // See `persist`.
  }
  audioSettingsStore.set({ ...DEFAULTS });
}
